import { useState, type MouseEvent } from 'react'
import { NO_STORE, formatMoney, inBasket, type FxRates, type Store, type TripItem, type TripTotals } from '@sp/shared'
import { formatAmount, formatCzkRounded, formatQty, inStore, itemsCount, storeShortName } from '../../lib/format.ts'
import { useDesktop } from '../../lib/useDesktop.ts'
import { DealTag, type DealLabel } from '../../components/OfferBadge.tsx'
import { BasketHead, BasketRow, Button, IconButton, ShopItem } from '../../components/ui.tsx'
import { nextStore, storeOfItem, type Section } from './shared.ts'

/** Kolik koupenych polozek je videt, nez se kosik rozbali. */
const BASKET_PREVIEW = 3

export function basketMeta(item: TripItem, store: Store | null): { text: string; strong: boolean } {
  const currency = item.expectedCurrency ?? store?.currency ?? null
  if (item.status === 'found_other_price' && item.actualPriceMinor !== null && currency) {
    const expected = item.expectedPriceMinor !== null ? ` místo ${formatAmount(item.expectedPriceMinor)}` : ''
    return { text: `${formatMoney(item.actualPriceMinor, currency)}${expected}`, strong: true }
  }
  return { text: formatQty(item.qty, item.unit), strong: false }
}

/** „zbývá 1 položka", „zbývají 3 položky", „zbývá 5 položek". */
export function remainingText(n: number): string {
  return `${n >= 2 && n <= 4 ? 'zbývají' : 'zbývá'} ${itemsCount(n)}`
}

/** Soucty v obchode: velky soucet patri obchodu, ve kterem se prave nakupuje. */
export function shopTotals(sections: readonly Section[], totals: TripTotals, rates: FxRates) {
  const withStore = sections.filter((section) => section.store !== null && section.items.length > 0)
  const current =
    withStore.find((section) => section.items.some((i) => i.status === 'pending')) ?? withStore[withStore.length - 1]
  const currentTotal = current ? totals.byStore[current.key] : undefined
  const convertible = Object.values(totals.byStore).every(
    (t) => t.currency === 'CZK' || (t.currency === 'EUR' ? rates.eurCzk : rates.plnCzk) !== null,
  )
  const onlyCzk = Object.values(totals.byStore).every((t) => t.currency === 'CZK')
  const czkLine =
    convertible && !onlyCzk && totals.czkMinor !== null && Object.keys(totals.byStore).length > 0
      ? `≈ ${formatCzkRounded(totals.czkMinor)}`
      : null
  const others = withStore
    .filter((section) => section !== current)
    .map(
      (section) =>
        `${storeShortName(section.title)} ${formatMoney(totals.byStore[section.key]?.minor ?? 0, section.store?.currency ?? 'CZK')}`,
    )
  // Nakup „kdekoli" nema obchod — soucet je podle meny polozek.
  const loose = totals.byStore[NO_STORE]
  const bigTotal = currentTotal
    ? formatMoney(currentTotal.minor, currentTotal.currency)
    : current?.store
      ? formatMoney(0, current.store.currency)
      : loose
        ? formatMoney(loose.minor, loose.currency)
        : null
  return {
    withStore,
    current,
    bigTotal,
    czkLine,
    footerMeta: [...(withStore.length > 1 ? others : []), czkLine].filter(Boolean).join(' · '),
  }
}

export function shopSectionMeta(section: Section, totals: TripTotals): string {
  const basket = section.items.filter(inBasket)
  const total = totals.byStore[section.key]
  return `${basket.length} z ${section.items.length} v košíku${
    total && total.counted > 0 ? ` · ${formatMoney(total.minor, total.currency)}` : ''
  }`
}

/**
 * Obchod pri nakupovani: velke kolecko = mam v kosiku, sipka otevre detail.
 * Nenalezena polozka nabidne dalsi obchod na trase a detail ji zustava.
 */
export function ShopSectionBody({
  section,
  route,
  stores,
  nameOf,
  flashClass,
  dealLabel,
  onToggle,
  onMove,
  onOpen,
  onItemMenu,
}: {
  /** Prave tlacitko na polozce (pocitac). */
  onItemMenu?: ((item: TripItem) => (event: MouseEvent<HTMLElement>) => void) | undefined
  section: Section
  route: readonly Store[]
  stores: readonly Store[]
  nameOf(userId: string | null): string | null
  flashClass(itemId: string): string | undefined
  dealLabel(item: TripItem): DealLabel | null
  onToggle(item: TripItem): void
  onMove(item: TripItem, store: Store): void
  onOpen(item: TripItem): void
}) {
  const desktop = useDesktop()
  const [basketOpen, setBasketOpen] = useState(true)
  const [expanded, setExpanded] = useState(false)
  const open = section.items.filter((i) => !inBasket(i))
  const basket = section.items.filter(inBasket)
  const next = section.store ? nextStore(route, section.store.id) : undefined
  const shown = expanded ? basket : basket.slice(0, BASKET_PREVIEW)
  const hidden = basket.length - shown.length
  const toggleLabel = desktop ? 'Kliknutím dáš do košíku' : 'Ťuknutím dáš do košíku'

  return (
    <>
      {open.map((it) => {
        const store = storeOfItem(it, stores)
        const more = (
          <IconButton icon="chevron_right" tone="muted" label={`Otevřít: ${it.name}`} onClick={() => onOpen(it)} />
        )
        if (it.status === 'not_found') {
          const who = nameOf(it.checkedBy)
          return (
            <ShopItem
              key={it.id}
              name={it.name}
              state="notfound"
              onContextMenu={onItemMenu?.(it)}
              sub={`Nemají${who ? ` · ${who}` : ''}`}
              toggleLabel={toggleLabel}
              onToggle={() => onToggle(it)}
              trailing={
                <span className="shop-item-actions">
                  {next && (
                    <Button compact onClick={() => onMove(it, next)}>
                      {`Zkusit ${inStore(next.name)}`}
                    </Button>
                  )}
                  {more}
                </span>
              }
            />
          )
        }
        const currency = it.expectedCurrency ?? store?.currency ?? null
        const price = it.expectedPriceMinor !== null && currency ? formatMoney(it.expectedPriceMinor, currency) : null
        const label = dealLabel(it)
        const text = [formatQty(it.qty, it.unit), price].filter(Boolean).join(' · ')
        return (
          <ShopItem
            key={it.id}
            name={it.name}
            state="pending"
            onContextMenu={onItemMenu?.(it)}
            className={flashClass(it.id)}
            sub={
              label ? (
                <span className="offer-sub">
                  <span>{text}</span>
                  <DealTag label={label} />
                </span>
              ) : (
                text
              )
            }
            toggleLabel={toggleLabel}
            onToggle={() => onToggle(it)}
            trailing={more}
          />
        )
      })}
      {basket.length > 0 && (
        <>
          <BasketHead label={`V košíku (${basket.length})`} open={basketOpen} onToggle={() => setBasketOpen((v) => !v)} />
          {basketOpen && (
            <div className="stack-8">
              {shown.map((it) => {
                const meta = basketMeta(it, storeOfItem(it, stores))
                return (
                  <BasketRow
                    key={it.id}
                    name={it.name}
                    meta={meta.text}
                    strong={meta.strong}
                    who={nameOf(it.checkedBy)}
                    onClick={() => onOpen(it)}
                  />
                )
              })}
              {hidden > 0 && (
                <Button kind="quiet" compact className="self-start" onClick={() => setExpanded(true)}>
                  {`a ${hidden} ${hidden >= 5 ? 'dalších' : 'další'}`}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </>
  )
}
